import { Box, InputAdornment, MenuItem, Select, TextField } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { Country } from 'country-state-city'
import { ICountry } from 'country-state-city/lib/interface'
import {
    ChangeEvent,
    FC,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { SYNDEO_COUNTRY_CODE } from '../../../utils/constants'
import { COUNTRY_NAMES } from '../../../utils/constants/country-name-translations'
import { conditionalClasses } from '../../../utils/helper-functions'

import { OnboardingFormContext } from '../../../providers/contexts/OnboardingForm'
import './phone-code-selection.scss'

interface PhoneCodeSelectionType {
    value: string | undefined
    countrySelected: ICountry | undefined
    touched?: boolean
    errors?: string
    disabled?: boolean
    fieldName?: string
    handleChange: (e: string) => void
    handleCountryChange?: (country: ICountry | undefined) => void
}

const phoneCodeFormat = (phoneCode: string) => {
    const prefix = '+'
    return !phoneCode.startsWith(prefix) ? `${prefix}${phoneCode}` : phoneCode
}

const SyndeoCountries = Object.values(
    (Country.getAllCountries() as ICountry[])
        .filter((item) =>
            SYNDEO_COUNTRY_CODE.some((code) => item.isoCode === code)
        )
        .map((item) => {
            if (item.phonecode.includes('and')) {
                return {
                    ...item,
                    phonecode: phoneCodeFormat(
                        item.phonecode.split(' and ')[0].split('-')[0]
                    ),
                }
            } else {
                return {
                    ...item,
                    phonecode: phoneCodeFormat(item.phonecode),
                }
            }
        })
        .sort((item1, item2) => item1.phonecode.localeCompare(item2.phonecode))
        .reduce((accumulator, currentCountry) => {
            if (accumulator[currentCountry.phonecode]) {
                accumulator[currentCountry.phonecode] = {
                    ...accumulator[currentCountry.phonecode],
                    flag: `${currentCountry.flag} ${
                        accumulator[currentCountry.phonecode].flag
                    } `,
                    name: ` ${currentCountry.name} & ${
                        accumulator[currentCountry.phonecode].name
                    }`,
                    isoCode: `${currentCountry.isoCode}/${
                        accumulator[currentCountry.phonecode].isoCode
                    }`,
                }
            } else {
                accumulator[currentCountry.phonecode] = currentCountry
            }
            return accumulator
        }, {} as Record<string, ICountry>)
)

const PhoneCodeSelection: FC<PhoneCodeSelectionType> = ({
    value,
    countrySelected,
    touched,
    errors,
    disabled = false,
    fieldName,
    handleChange,
    handleCountryChange,
}) => {
    const [country, setCountry] = useState<ICountry | undefined>(() => {
        const phoneCode = `${
            countrySelected && !countrySelected.phonecode.startsWith('+') && '+'
        }${countrySelected?.phonecode}`
        return SyndeoCountries.find(
            (country) => country.phonecode === phoneCode
        )
    })

    const { mlSignupError, setMlSignupError } = useContext(
        OnboardingFormContext
    )

    /**
     * based on the value the following logic is checked
     * if `value` exists and starts with `+`
     * [true] --> if [`country` does not exists] or  [`value` does not start with `country.phonecode`]
     * [true] --> [true] --> set `country` as the correct country and return `value` without code
     * [true] --> [false] --> `value` matches `country` so return `value` without code
     * [false] --> return `value` as it is
     */
    const fieldValue = useMemo(() => {
        if (value?.startsWith('+')) {
            if ((country && !value.startsWith(country.phonecode)) || !country) {
                const numberCountry = SyndeoCountries.find((country) =>
                    value.startsWith(country.phonecode)
                )
                if (numberCountry) {
                    setCountry(numberCountry)
                    return value
                } else {
                    return value.substring(1)
                }
            } else {
                return value.substring(country.phonecode.length)
            }
        } else {
            return value
        }
    }, [value, country])

    const handleChangePhoneCode = (event: SelectChangeEvent) => {
        const countryCode = SyndeoCountries.find(
            (country) => country.isoCode === event.target.value
        )
        setCountry(countryCode)
        countryCode && handleChange(`${countryCode.phonecode}${fieldValue}`)
        handleCountryChange?.(countryCode)
    }

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (mlSignupError) setMlSignupError('')
        const countryCode =
            country?.phonecode && phoneCodeFormat(country.phonecode)
        const phoneNumber = `${
            countryCode || ''
        } ${e.target.value.trimStart()}`.trimStart()
        handleChange(phoneNumber)
    }

    useEffect(() => {
        if (countrySelected) {
            const phoneCode = phoneCodeFormat(countrySelected.phonecode)
            const matchedCountry = SyndeoCountries.find(
                (country) => country.phonecode === phoneCode
            )
            setCountry(matchedCountry)
        }
    }, [countrySelected])

    return (
        <>
            <Box
                className={`box-inline ${conditionalClasses(
                    disabled,
                    'disabled',
                    ''
                )} ${conditionalClasses(!!touched && !!errors, 'errorBorder')}`}
            >
                <Box className="box-select">
                    <Select
                        className="select-country"
                        name="isocode"
                        placeholder="Country"
                        value={country?.isoCode}
                        autoWidth
                        variant="standard"
                        onChange={handleChangePhoneCode}
                        renderValue={(selected) =>
                            selected === country?.isoCode && country?.flag
                        }
                        MenuProps={{
                            className: 'phone-code-selection-menu',
                        }}
                    >
                        {SyndeoCountries.map((option) => (
                            <MenuItem
                                key={`result-${option.name}`}
                                value={option.isoCode}
                                className="country-name"
                            >
                                <span> {option.flag}</span>
                                {(COUNTRY_NAMES as any)[option.isoCode]}(
                                {option.phonecode})
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box className="box-input">
                    <TextField
                        type="tel"
                        name={fieldName ?? 'country'}
                        variant="standard"
                        value={fieldValue}
                        onChange={onChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {country &&
                                        phoneCodeFormat(country?.phonecode)}
                                </InputAdornment>
                            ),
                        }}
                        disabled={disabled}
                    />
                </Box>
            </Box>
            {touched && !!errors && <div className="error-txt">{errors}</div>}
        </>
    )
}

export default PhoneCodeSelection
